import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { Message, Conversation } from '../types';
import { useAuth } from './AuthContext';
import { useAppDispatch, useAppState, updateConversationLastMessage, incrementUnseenCount, addConversation, loadEventData } from './AppContext';
import { authenticatedFetch } from './auth';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    currentConversationId: number | null;
    setCurrentConversationId: (id: number | null) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { token, isAuthenticated, logout } = useAuth();
    const dispatch = useAppDispatch();
    const { eventData } = useAppState();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);

    // Use ref to access current conversation ID in socket callback
    const currentConversationIdRef = useRef<number | null>(null);
    // Use ref for eventData to avoid stale closures
    const eventDataRef = useRef(eventData);
    // Track which conversations we've joined
    const joinedConversationsRef = useRef<Set<number>>(new Set());

    useEffect(() => {
        currentConversationIdRef.current = currentConversationId;
    }, [currentConversationId]);

    useEffect(() => {
        eventDataRef.current = eventData;
    }, [eventData]);

    // Function to fetch a single conversation by ID
    const fetchConversation = async (conversationId: number): Promise<Conversation | null> => {
        try {
            const response = await authenticatedFetch(`${API_URL}/api/chat`);
            if (!response.ok) return null;
            const conversations: Conversation[] = await response.json();
            return conversations.find(c => c.id === conversationId) || null;
        } catch (error) {
            console.error('Error fetching conversation:', error);
            return null;
        }
    };

    // Join all existing conversations when socket connects or conversations change
    useEffect(() => {
        if (!socket || !isConnected || !eventData?.chat_conversations) return;

        const conversationsToJoin = eventData.chat_conversations;

        conversationsToJoin.forEach(conv => {
            if (!joinedConversationsRef.current.has(conv.id)) {
                socket.emit('join_conversation', conv.id);
                joinedConversationsRef.current.add(conv.id);
            }
        });
    }, [socket, isConnected, eventData?.chat_conversations]);

    useEffect(() => {
        if (!isAuthenticated || !token) {
            // Disconnect if not authenticated
            if (socket) {
                socket.disconnect();
                setSocket(null);
                setIsConnected(false);
                joinedConversationsRef.current.clear();
            }
            return;
        }

        // Create socket connection
        const newSocket = io(API_URL, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });

        newSocket.on('connect', () => {
            console.log('Socket connected globally');
            setIsConnected(true);
            // Reset joined conversations on reconnect - they'll be re-joined by the useEffect above
            joinedConversationsRef.current.clear();
            // Mark app as online when socket connects
            try {
                dispatch({ type: 'SET_ONLINE', payload: true } as any);
            } catch (e) {
                console.warn('Failed to dispatch SET_ONLINE on socket connect', e);
            }
            // Also fire a synthetic window 'online' event so listeners using window.addEventListener('online') run
            try {
                if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(new Event('online'));
                }
            } catch (e) {
                console.warn('Failed to dispatch window online event on socket connect', e);
            }
        });

        newSocket.on('disconnect', () => {
            console.log('Socket disconnected');
            setIsConnected(false);
            // Mark app as offline when socket disconnects
            try {
                dispatch({ type: 'SET_ONLINE', payload: false } as any);
            } catch (e) {
                console.warn('Failed to dispatch SET_ONLINE on socket disconnect', e);
            }
            // Also fire a synthetic window 'offline' event so listeners using window.addEventListener('offline') run
            try {
                if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(new Event('offline'));
                }
            } catch (e) {
                console.warn('Failed to dispatch window offline event on socket disconnect', e);
            }
        });

        newSocket.on('connect_error', (err) => {
            console.error('Socket connection error:', err);
            setIsConnected(false);
            // Mark app as offline on connection error
            try {
                dispatch({ type: 'SET_ONLINE', payload: false } as any);
            } catch (e) {
                console.warn('Failed to dispatch SET_ONLINE on socket connect_error', e);
            }
            // Also fire a synthetic window 'offline' event so polling/ping logic runs
            try {
                if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(new Event('offline'));
                }
            } catch (e) {
                console.warn('Failed to dispatch window offline event on socket connect_error', e);
            }
        });

        // Handle errors from server
        newSocket.on('error', (error: { message: string }) => {
            console.error('Socket error:', error.message);
        });

        // Global message handler
        newSocket.on('new_message', async (message: Message) => {
            const currentEventData = eventDataRef.current;
            const existingConversation = currentEventData?.chat_conversations?.find(
                c => c.id === message.conversationId
            );

            // If conversation doesn't exist in state, fetch it first
            if (!existingConversation) {
                const fetchedConversation = await fetchConversation(message.conversationId);
                if (fetchedConversation) {
                    addConversation(dispatch, fetchedConversation);
                    // Join the new conversation channel
                    newSocket.emit('join_conversation', fetchedConversation.id);
                    joinedConversationsRef.current.add(fetchedConversation.id);
                }
            } else {
                // Update the conversation's last message in global state
                updateConversationLastMessage(dispatch, message.conversationId, message);
            }

            // If user is NOT viewing this conversation, increment unseen count
            if (currentConversationIdRef.current !== message.conversationId) {
                incrementUnseenCount(dispatch, message.conversationId);
            } else {
                // User is viewing this conversation, mark as seen
                authenticatedFetch(`${API_URL}/api/chat/${message.conversationId}/seen`, {
                    method: 'POST'
                }).catch(console.error);
            }
        });

        // Handler for new conversation notifications (when another user starts a chat with us)
        newSocket.on('new_conversation', async (data: { conversationId: number; message: Message }) => {
            const { conversationId, message } = data;

            // Fetch the full conversation data
            const fetchedConversation = await fetchConversation(conversationId);
            if (fetchedConversation) {
                addConversation(dispatch, fetchedConversation);
                // Join the new conversation channel to receive future messages
                newSocket.emit('join_conversation', conversationId);
                joinedConversationsRef.current.add(conversationId);
            }

            // Increment unseen count since this is a new message
            if (currentConversationIdRef.current !== conversationId) {
                incrementUnseenCount(dispatch, conversationId);
            }
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
            joinedConversationsRef.current.clear();
        };
    }, [isAuthenticated, token, dispatch]);

    return (
        <SocketContext.Provider value={{
            socket,
            isConnected,
            currentConversationId,
            setCurrentConversationId
        }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (): SocketContextType => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};
